import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/c9c33317-dd4d-46a1-86d4-5b8ff653a1d8.png" alt="VIGOR LTDA Logo" className="h-10 w-10 object-contain" />
            <h1 className="text-2xl font-bold text-[#1A1A1A]">VIGOR LTDA</h1>
          </div>
          <nav className="flex items-center gap-10">
            <button onClick={() => scrollToSection('home')} className="text-base font-light text-[#1A1A1A] hover:font-medium transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap">Home</button>
            <button onClick={() => scrollToSection('about')} className="text-base font-light text-[#1A1A1A] hover:font-medium transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap">About</button>
            <button onClick={() => scrollToSection('products')} className="text-base font-light text-[#1A1A1A] hover:font-medium transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap">Products</button>
            <button onClick={() => scrollToSection('features')} className="text-base font-light text-[#1A1A1A] hover:font-medium transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap">Features</button>
            <button onClick={() => scrollToSection('contact')} className="text-base font-light text-[#1A1A1A] hover:font-medium transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap">Contact</button>
          </nav>
        </div>
        <div className="h-px bg-[#E5E5E5]"></div>
      </header>

      {/* Hero Section */}
      <section id="home" className="min-h-[85vh] pt-32 pb-20 px-8">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-12 gap-12 items-center">
            <div className="col-span-5">
              <p className="text-xs uppercase tracking-[2px] text-[#8A8A8A] mb-6">MOBILE INNOVATION</p>
              <h2 className="mb-8">
                <span className="block text-7xl font-light font-serif text-[#1A1A1A] leading-tight">Crafting Premium</span>
                <span className="block text-7xl font-bold italic text-[#1A1A1A] leading-tight mt-2">iOS Applications</span>
                <span className="block text-7xl font-normal font-serif text-[#1A1A1A] leading-tight mt-2">for Modern Users</span>
              </h2>
              <p className="text-lg leading-relaxed text-[#4A4A4A] mt-8 max-w-lg">
                We are a team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
              </p>
            </div>
            <div className="col-span-7">
              <div className="relative rounded-[32px] overflow-hidden bg-gradient-to-br from-blue-100 via-purple-50 to-pink-50 p-12 h-[600px] flex items-center justify-center">
                <img 
                  src="https://public.readdy.ai/ai/img_res/f994aa54e99fd4fd82a39754ea3eea12.jpg" 
                  alt="Mobile Innovation" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-8">
        <div className="max-w-[1400px] mx-auto">
          <div className="bg-[#F5F5F5] rounded-[24px] py-20 px-16">
            <div className="max-w-[900px] mx-auto">
              <p className="text-sm uppercase tracking-[3px] text-[#8A8A8A] mb-8">ABOUT</p>
              <h3 className="text-5xl font-serif text-[#2D2D2D] mb-6 leading-tight">
                <span className="font-normal">Expert Team Building</span><br />
                <span className="italic">Tomorrow's Solutions</span>
              </h3>
              <p className="text-xl leading-relaxed text-[#2D2D2D] mt-10">
                At VIGOR LTDA, we combine technical expertise with creative innovation to deliver exceptional iOS applications. Our dedicated team focuses on creating solutions that not only meet but exceed user expectations. With a deep understanding of mobile security and system optimization, we develop products that enhance device performance while maintaining the highest standards of user privacy and data protection.
              </p>
              <p className="text-xl leading-relaxed text-[#2D2D2D] mt-6">
                Our commitment to excellence drives us to continuously improve and adapt to the evolving needs of modern mobile users. We believe in creating applications that are intuitive, powerful, and reliable, ensuring that every user enjoys a seamless and secure experience on their iOS devices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 px-8 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-6xl mb-4">
              <span className="font-bold text-[#1A1A1A]">Our Products</span>
              <span className="font-bold text-[#E63946]"> VPN & Cleaner</span>
            </h3>
            <p className="text-base text-[#8A8A8A] mt-4">Powerful solutions for enhanced security and performance</p>
          </div>
          
          <div className="grid grid-cols-2 gap-8">
            {/* VPN Product Card */}
            <div className="rounded-[28px] overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_48px_rgba(0,0,0,0.12)] transition-all duration-300 hover:-translate-y-2">
              <div className="h-[400px] bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] flex items-center justify-center p-12 relative overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20VPN%20mobile%20app%20interface%20mockup%20with%20shield%20protection%20icon%20network%20connection%20visualization%20floating%20UI%20elements%20on%20deep%20blue%20gradient%20background%20clean%20minimalist%20iOS%20design%20professional%20security%20concept&width=600&height=400&seq=vpn001&orientation=landscape" 
                  alt="VPN Application" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-white p-8">
                <h4 className="text-2xl font-bold text-[#1A1A1A] mb-3">VPN Security</h4>
                <p className="text-[15px] leading-relaxed text-[#6B6B6B]">
                  Protect your online privacy with our advanced VPN solution. Secure browsing, encrypted connections, and global server access for complete peace of mind.
                </p>
              </div>
            </div>

            {/* Cleaner Product Card */}
            <div className="rounded-[28px] overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_48px_rgba(0,0,0,0.12)] transition-all duration-300 hover:-translate-y-2">
              <div className="h-[400px] bg-gradient-to-br from-[#065F46] to-[#10B981] flex items-center justify-center p-12 relative overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20iOS%20cleaner%20app%20interface%20mockup%20with%20storage%20optimization%20icons%20performance%20boost%20visualization%20floating%20UI%20elements%20on%20emerald%20green%20gradient%20background%20clean%20minimalist%20design%20professional%20system%20optimization&width=600&height=400&seq=cleaner001&orientation=landscape" 
                  alt="Cleaner Application" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-white p-8">
                <h4 className="text-2xl font-bold text-[#1A1A1A] mb-3">System Cleaner</h4>
                <p className="text-[15px] leading-relaxed text-[#6B6B6B]">
                  Optimize your device performance with intelligent cleaning. Remove junk files, free up storage space, and keep your iOS device running smoothly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-28 px-8 bg-[#FAF9F6]">
        <div className="max-w-[1400px] mx-auto">
          <h3 className="text-5xl font-bold font-serif text-center text-[#1A1A1A] mb-16">Key Features</h3>
          
          <div className="grid grid-cols-4 gap-6">
            {/* Feature 1 */}
            <div className="rounded-[20px] bg-[#D1FAE5] p-8 hover:-translate-y-2 hover:shadow-lg transition-all duration-300 cursor-pointer">
              <div className="h-32 flex items-center justify-center mb-6">
                <div className="w-20 h-20 flex items-center justify-center">
                  <i className="ri-shield-check-line text-5xl text-[#065F46]"></i>
                </div>
              </div>
              <h4 className="text-xl font-medium text-[#1A1A1A] mb-3">Advanced Security</h4>
              <p className="text-sm leading-relaxed text-[#4A4A4A]">
                Military-grade encryption and secure protocols to protect your data and privacy at all times.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="rounded-[20px] bg-[#FCE7F3] p-8 hover:-translate-y-2 hover:shadow-lg transition-all duration-300 cursor-pointer">
              <div className="h-32 flex items-center justify-center mb-6">
                <div className="w-20 h-20 flex items-center justify-center">
                  <i className="ri-speed-up-line text-5xl text-[#BE185D]"></i>
                </div>
              </div>
              <h4 className="text-xl font-medium text-[#1A1A1A] mb-3">Fast Performance</h4>
              <p className="text-sm leading-relaxed text-[#4A4A4A]">
                Optimized algorithms ensure lightning-fast speeds without compromising on security or reliability.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="rounded-[20px] bg-[#E0E7FF] p-8 hover:-translate-y-2 hover:shadow-lg transition-all duration-300 cursor-pointer">
              <div className="h-32 flex items-center justify-center mb-6">
                <div className="w-20 h-20 flex items-center justify-center">
                  <i className="ri-user-smile-line text-5xl text-[#3730A3]"></i>
                </div>
              </div>
              <h4 className="text-xl font-medium text-[#1A1A1A] mb-3">User Friendly</h4>
              <p className="text-sm leading-relaxed text-[#4A4A4A]">
                Intuitive interface designed for seamless navigation and effortless control of all features.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="rounded-[20px] bg-[#FED7AA] p-8 hover:-translate-y-2 hover:shadow-lg transition-all duration-300 cursor-pointer">
              <div className="h-32 flex items-center justify-center mb-6">
                <div className="w-20 h-20 flex items-center justify-center">
                  <i className="ri-customer-service-2-line text-5xl text-[#9A3412]"></i>
                </div>
              </div>
              <h4 className="text-xl font-medium text-[#1A1A1A] mb-3">24/7 Support</h4>
              <p className="text-sm leading-relaxed text-[#4A4A4A]">
                Dedicated support team available around the clock to assist you with any questions or concerns.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Footer Section */}
      <section id="contact" className="bg-[#1F1F1F] rounded-t-[40px] py-20 px-8">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-4 gap-12 mb-16">
            {/* Column 1 - Company Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="https://public.readdy.ai/ai/img_res/c9c33317-dd4d-46a1-86d4-5b8ff653a1d8.png" alt="VIGOR LTDA Logo" className="h-8 w-8 object-contain" />
                <h4 className="text-3xl font-bold text-white">VIGOR LTDA</h4>
              </div>
              <p className="text-base text-[#A0A0A0] leading-relaxed">
                Crafting premium iOS applications for modern users worldwide.
              </p>
            </div>

            {/* Column 2 - Address */}
            <div>
              <h5 className="text-lg font-medium text-white mb-4">Location</h5>
              <div className="space-y-2">
                <p className="text-[15px] text-[#A0A0A0] leading-relaxed flex items-start gap-2">
                  <i className="ri-map-pin-line text-lg mt-0.5"></i>
                  <span>Faz. FAZENDA CACHOEIRINHA S/N S N</span>
                </p>
                <p className="text-[15px] text-[#A0A0A0] leading-relaxed pl-7">CACHOEIRA DE PAJEU</p>
                <p className="text-[15px] text-[#A0A0A0] leading-relaxed pl-7">39980-000</p>
                <p className="text-[15px] text-[#A0A0A0] leading-relaxed pl-7">Brazil</p>
              </div>
            </div>

            {/* Column 3 - Contact Methods */}
            <div>
              <h5 className="text-lg font-medium text-white mb-4">Get in Touch</h5>
              <div className="space-y-3">
                <p className="text-[15px] text-[#A0A0A0] leading-relaxed flex items-center gap-2">
                  <i className="ri-mail-line text-lg"></i>
                  <span>information@vigorltda.com</span>
                </p>
                <p className="text-[15px] text-[#A0A0A0] leading-relaxed flex items-center gap-2">
                  <i className="ri-phone-line text-lg"></i>
                  <span>+1 4055542253</span>
                </p>
              </div>
            </div>

            {/* Column 4 - Quick Links */}
            <div>
              <h5 className="text-lg font-medium text-white mb-4">Quick Links</h5>
              <div className="space-y-3">
                <button onClick={() => scrollToSection('home')} className="block text-[15px] text-[#A0A0A0] hover:text-white transition-colors duration-300 cursor-pointer whitespace-nowrap">Home</button>
                <button onClick={() => scrollToSection('about')} className="block text-[15px] text-[#A0A0A0] hover:text-white transition-colors duration-300 cursor-pointer whitespace-nowrap">About</button>
                <button onClick={() => scrollToSection('products')} className="block text-[15px] text-[#A0A0A0] hover:text-white transition-colors duration-300 cursor-pointer whitespace-nowrap">Products</button>
                <button onClick={() => scrollToSection('features')} className="block text-[15px] text-[#A0A0A0] hover:text-white transition-colors duration-300 cursor-pointer whitespace-nowrap">Features</button>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="pt-8 border-t border-[#3A3A3A] flex items-center justify-between">
            <p className="text-sm text-[#6B6B6B]">© 2025 VIGOR LTDA. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-[#6B6B6B] hover:text-white transition-colors duration-300 cursor-pointer whitespace-nowrap">
              Powered by Readdy
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
